/**
 * Footer Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Site-wide footer with navigation links, contact info, social media, and legal information.
 * Consistent branding and accessibility across all pages.
 * 
 * Features:
 * - Logo branding
 * - Navigation links (Destinations, Services, Collections, Contact, About)
 * - Contact information (email, phone, address)
 * - Social media links
 * - Newsletter signup
 * - Legal links (Privacy, Terms, Cookies)
 * - Copyright information
 * - Responsive design (mobile-friendly)
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - React Router 7 (Link navigation)
 * - Font Awesome 6.5 (icons)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { trackContactClick } from '../lib/analytics';
import { COMPANY_INFO } from '../config/company';
import { openCookiePreferences } from '../lib/consent';
import { NewsletterSignup } from './NewsletterSignup';
import { ObfuscatedEmail } from './ObfuscatedEmail';
import { ProIcon } from './ProIcon';

export const Footer = () => {
  const location = useLocation();
  const showFooterNewsletter =
    location.pathname !== '/' &&
    location.pathname !== '/destinations' &&
    !location.pathname.startsWith('/property/');

  return (
    <footer className="bg-slate-900 text-white pt-10 sm:pt-20 pb-8 sm:pb-10 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer keeps a lighter signup version on pages that do not already show a stronger newsletter block */}
        {showFooterNewsletter ? (
          <div className="mb-10 sm:mb-14">
            <NewsletterSignup
              variant="footer"
              source="footer"
              eyebrow="Direct Booking Updates"
              title="Get Marbella Deals Without Refreshing The Site"
              description="A lower-key option for guests still comparing dates. We only send the openings and offers worth knowing about."
              buttonLabel="Join Free"
              tags={['footer', 'sitewide']}
            />
          </div>
        ) : null}

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12 mb-10 sm:mb-16">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1 space-y-4 sm:space-y-6">
            <Logo variant="full" color="white" className="h-11 sm:h-13 w-auto" />
            <div className="text-left text-sm sm:text-sm font-medium text-white/90 leading-relaxed normal-case">
              SUNMARBELLA - Book direct up to 20% cheaper, real local support for inspected Marbella homes.
            </div>
            <div className="flex gap-3">
              <a
                href="https://instagram.com/sunmarbella"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContactClick('instagram', 'footer')}
                className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-xl hover:bg-brand-sun hover:scale-110 transition-all duration-200"
                aria-label="SUNMARBELLA on Instagram"
              >
                <ProIcon icon="instagram" family="brands" className="text-base text-white" />
              </a>
              <a
                href="https://facebook.com/sunmarbella"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContactClick('facebook', 'footer')}
                className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-xl hover:bg-brand-sun hover:scale-110 transition-all duration-200"
                aria-label="SUNMARBELLA on Facebook"
              >
                <ProIcon icon="facebook" family="brands" className="text-base text-white" />
              </a>
              <a
                href="https://tiktok.com/@sunmarbella"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContactClick('tiktok', 'footer')}
                className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-xl hover:bg-brand-sun hover:scale-110 transition-all duration-200"
                aria-label="SUNMARBELLA on TikTok"
              >
                <ProIcon icon="tiktok" family="brands" className="text-base text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm sm:text-lg font-bold mb-3 sm:mb-6">Quick Links</h4>
            <ul className="space-y-2.5 sm:space-y-4 text-slate-400 text-sm">
              <li><Link to="/destinations" className="hover:text-brand-sun transition-colors">All Rentals</Link></li>
              <li><Link to="/collections" className="hover:text-brand-sun transition-colors">Collections</Link></li>
              <li><Link to="/services" className="hover:text-brand-sun transition-colors">Guest Services</Link></li>
              <li><Link to="/about" className="hover:text-brand-sun transition-colors">About</Link></li>
              <li><Link to="/contact" className="hover:text-brand-sun transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="text-sm sm:text-lg font-bold mb-3 sm:mb-6">Popular Areas</h4>
            <ul className="space-y-2.5 sm:space-y-4 text-slate-400 text-sm">
              <li><Link to="/marbella-golden-mile" className="hover:text-brand-sun transition-colors">Golden Mile</Link></li>
              <li><Link to="/nueva-andalucia" className="hover:text-brand-sun transition-colors">Nueva Andalucia</Link></li>
              <li><Link to="/puerto-banus" className="hover:text-brand-sun transition-colors">Puerto Banus</Link></li>
              <li><Link to="/elviria" className="hover:text-brand-sun transition-colors">Elviria</Link></li>
              <li><Link to="/sierra-blanca" className="hover:text-brand-sun transition-colors">Sierra Blanca</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm sm:text-lg font-bold mb-3 sm:mb-6">Speak to Our Team</h4>
            <ul className="space-y-2.5 sm:space-y-4 text-slate-400 text-sm">
              <li className="flex items-center gap-3 group">
                <span className="text-xl text-white group-hover:text-brand-sun transition-colors flex-shrink-0 flex items-center justify-center">
                  <ProIcon icon="phone-connection" family="light" />
                </span>
                <a href={COMPANY_INFO.phoneHref} className="hover:text-brand-sun transition-colors">
                  {COMPANY_INFO.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <span className="text-xl text-white group-hover:text-brand-sun transition-colors flex-shrink-0 flex items-center justify-center">
                  <ProIcon icon="envelope-open" family="light" />
                </span>
                <ObfuscatedEmail className="hover:text-brand-sun transition-colors" />
              </li>
              <li className="flex items-center gap-3 group">
                <span className="text-xl text-white group-hover:text-brand-sun transition-colors flex-shrink-0 flex items-center justify-center">
                  <ProIcon icon="circle-location-arrow" family="light" />
                </span>
                <span className="group-hover:text-brand-sun transition-colors">
                  {COMPANY_INFO.address.line1}, {COMPANY_INFO.address.line2}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 font-medium">
          <p>© SUNMARBELLA Holiday Rentals. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/booking-terms" className="hover:text-white transition-colors">Booking Terms</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy & Cookie Policy</Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="hover:text-white transition-colors flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-800"
              aria-label="Cookie Settings"
            >
              <ProIcon icon="cookie" family="solid" className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
